import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import languages from '../../../../../../helpers/utils/languages';

// En mode lecture seule (« Identique à la commande » actif par défaut),
// `validateSavFields` pouvait poser des erreurs sur des champs que le
// conseiller ne voyait jamais : cette branche ne rendait que du texte figé,
// sans jamais lire `formError`. D'où un bouton « Confirmer » muet sur une
// adresse dont `address1` est trop court (ex. numéro de rue seul).
// On affiche donc le message sous la valeur, comme le ferait `helperText` sur
// un TextField. Le bloc facturation, synchronisé sur la livraison
// (SavOrderForm.jsx useEffect `isUsingShippingAddress`), est couvert d'office.
const ReadOnlyAddressField = ({ label, error, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Typography color={error ? 'error.main' : 'text.primary'}>
      <b>{label} : </b>
      {children}
    </Typography>
    {error && (
      <Typography variant="caption" color="error.main">
        {error}
      </Typography>
    )}
  </Box>
);

const SetAddress = ({
  formData,
  orderDetails,
  handleFormChange,
  addressName,
  formError,
  ticket,
}) => {
  const [isUsingOrderAddress, setIsUsingOrderAddress] = useState(true);
  const keyName = `${addressName}Address`;
  const addressType = addressName === 'billing' ? 'facturation' : 'livraison';

  const handleSwitchChange = (value) => {
    if (value) {
      console.log('orderDetails', orderDetails);
      if (orderDetails !== null) {
        console.log('handleSwitchChange', value);
        handleFormChange({
          key: keyName,
          value: {
            address1: orderDetails?.addresses[addressName].line1,
            address2: orderDetails?.addresses[addressName].line2,
            postalCode: orderDetails?.addresses[addressName].postalCode,
            city: orderDetails?.addresses[addressName].city,
            country: orderDetails?.addresses[addressName].country,
          },
        });
      } else if (ticket) {
        console.log('ticket', ticket);
        const ticketBillingAddress =
          ticket?.contact?.addresses?.find((address) => address.isBilling) ||
          {};
        console.log('ticketBillingAddress', ticketBillingAddress);
        handleFormChange({
          key: keyName,
          value: {
            address1: ticketBillingAddress.line1,
            address2: ticketBillingAddress.line2,
            postalCode: ticketBillingAddress.zipCode,
            city: ticketBillingAddress.city,
            country: ticketBillingAddress.country.code2,
          },
        });
      }
      setIsUsingOrderAddress(value);
    } else {
      setIsUsingOrderAddress(value);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        mt: 3,
      }}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
      >
        <Typography
          color="primary"
          sx={{ fontSize: '1em', fontWeight: 'bold', mb: 1 }}
        >
          Adresse de {addressType}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isUsingOrderAddress}
              onChange={(e) => handleSwitchChange(e.target.checked)}
            />
          }
          label={
            orderDetails
              ? 'Identique à la commande'
              : 'Identique à la facturation'
          }
        />
      </Box>
      {isUsingOrderAddress ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ReadOnlyAddressField
              label="Adresse"
              error={formError[keyName]?.address1}
            >
              {formData[keyName].address1}
            </ReadOnlyAddressField>
          </Box>
          <ReadOnlyAddressField
            label="Complément d'adresse"
            error={formError[keyName]?.address2}
          >
            {formData[keyName].address2}
          </ReadOnlyAddressField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ReadOnlyAddressField
              label="Code postal"
              error={formError[keyName]?.postalCode}
            >
              {formData[keyName].postalCode}
            </ReadOnlyAddressField>
            <ReadOnlyAddressField
              label="Ville"
              error={formError[keyName]?.city}
            >
              {formData[keyName].city}
            </ReadOnlyAddressField>
            <ReadOnlyAddressField label="Pays">
              {formData[keyName].country && (
                <>
                  <span
                    className={`fi fi-${formData[
                      keyName
                    ].country.toLowerCase()}`}
                    style={{ marginRight: 8 }}
                  />
                  {languages.find(
                    (country) => country.code2 === formData[keyName].country
                  )?.country || ''}
                </>
              )}
            </ReadOnlyAddressField>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Adresse"
            value={formData[keyName].address1}
            onChange={(e) =>
              handleFormChange({
                key: `${keyName}.address1`,
                value: e.target.value,
              })
            }
            error={Boolean(formError[keyName]?.address1)}
            helperText={formError[keyName]?.address1}
            fullWidth
            size="small"
          />
          <TextField
            label="Complément d'adresse"
            value={formData[keyName].address2}
            onChange={(e) =>
              handleFormChange({
                key: `${keyName}.address2`,
                value: e.target.value,
              })
            }
            error={Boolean(formError[keyName]?.address2)}
            helperText={formError[keyName]?.address2}
            fullWidth
            size="small"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Code postal"
              value={formData[keyName].postalCode}
              onChange={(e) =>
                handleFormChange({
                  key: `${keyName}.postalCode`,
                  value: e.target.value,
                })
              }
              error={Boolean(formError[keyName]?.postalCode)}
              helperText={formError[keyName]?.postalCode}
              fullWidth
              size="small"
            />
            <TextField
              label="Ville"
              value={formData[keyName].city}
              onChange={(e) =>
                handleFormChange({
                  key: `${keyName}.city`,
                  value: e.target.value,
                })
              }
              error={Boolean(formError[keyName]?.city)}
              helperText={formError[keyName]?.city}
              fullWidth
              size="small"
            />
            <TextField
              // disabled
              label="Pays"
              value={
                languages.find(
                  (country) => country.code2 === formData[keyName].country
                )?.country || ''
              }
              fullWidth
              size="small"
              slotProps={{
                input: {
                  startAdornment: formData[keyName].country && (
                    <span
                      className={`fi fi-${formData[
                        keyName
                      ].country.toLowerCase()}`}
                      style={{ marginRight: 8 }}
                    ></span>
                  ),
                  readOnly: true,
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SetAddress;
